/**
 * Lexicanon post-recording summary surface. JSX moved as-is from the
 * meeting-detail route so the route stays orchestration + render gates.
 */
import { recordingModeOf, type TExperienceMode } from "@lexicanon/shared"
import { msg } from "@lingui/core/macro"
import { useLingui } from "@lingui/react"
import { Trans } from "@lingui/react/macro"
import { Alert, Button, Container, Group, Text } from "@mantine/core"
import { notifications } from "@mantine/notifications"
import type { QueryClient } from "@tanstack/react-query"
import { useCallback, useState } from "react"
import { ConvertRecordingModeModal } from "../../../../components/ConvertRecordingModeModal"
import { MeetingAccessModal } from "../../../../components/MeetingAccessModal"
import { MeetingRecordingMismatchBanner } from "../../../../components/MeetingRecordingMismatchBanner"
import { MeetingSummaryView } from "../../../../components/MeetingSummary"
import type { IEditContext } from "../../../../components/TemplateAnalysisView"
import { SpeakerColorsProvider } from "../../../../contexts/SpeakerColors"
import type { CompanyApi } from "../../../../hooks/useCompanyApi"
import { useExperienceMode } from "../../../../hooks/useExperienceMode"
import { convertMeetingRecordingMode, type IJargonSuggestion, setMeetingLanguage } from "../../../../lib/api"
import { canReprocess } from "../../../../lib/meeting-reprocess"
import { notifyError } from "../../../../lib/notify"
import { queryKeys } from "../../../../lib/query-keys"
import { normalizeAnalysis, speakerLabelDisplayMap } from "../../../../lib/speaker-mapping"
import type { ISpeakerVMResult } from "../../../../lib/speaker-vm"
import type { IMeetingAnalysis, IMeetingDetail } from "../../../../lib/types/meeting"
import { useMeetingLifecycleActions } from "./useMeetingLifecycleActions"
import type { IAvailableTranslation } from "./useMeetingTranslation"

export interface IMeetingDetailLexSummaryProps {
  meeting: IMeetingDetail
  meetingId: string
  orgId: string | null
  company: CompanyApi | null
  companyId: string | undefined
  queryClient: QueryClient
  speakerNameList: string[]
  speakerVMs: ISpeakerVMResult
  speakerColorMap: Map<string, string>
  displayAnalysis: Record<string, unknown>
  accessOpened: boolean
  closeAccess: () => void
  openAccess: () => void
  canManageAccess: boolean
  isOrgAdmin: boolean
  isOrganizer: boolean
  reveal: boolean
  seekSeconds?: number
  /** Narrow-layout tab from `?tab=` on the meeting route. */
  initialTab?: "summary" | "details"
  sourceLocale: string
  analysisLocale: string
  availableTranslations: IAvailableTranslation[]
  isTranslating: boolean
  hasTranslationError: boolean
  onLocaleChange: (locale: string) => void
  jargonSuggestions: IJargonSuggestion[]
  onAcceptJargon: (sid: string, term?: string) => void
  onDismissJargon: (sid: string) => void
  jargonBusyId: string | null
  refetch: () => unknown
  navigateHome: () => void
  /** Signed-in user's default for new meetings (UI Mode). */
  experienceMode?: TExperienceMode
}

export function MeetingDetailLexSummary({
  meeting,
  meetingId,
  orgId,
  company,
  companyId,
  queryClient,
  speakerNameList,
  speakerVMs,
  speakerColorMap,
  displayAnalysis,
  accessOpened,
  closeAccess,
  openAccess,
  canManageAccess,
  isOrgAdmin,
  isOrganizer,
  reveal,
  seekSeconds,
  initialTab,
  sourceLocale,
  analysisLocale,
  availableTranslations,
  isTranslating,
  hasTranslationError,
  onLocaleChange,
  jargonSuggestions,
  onAcceptJargon,
  onDismissJargon,
  jargonBusyId,
  refetch,
  navigateHome,
  experienceMode = "full",
}: IMeetingDetailLexSummaryProps): React.JSX.Element {
  const [isChangingSourceLanguage, setIsChangingSourceLanguage] = useState(false)
  const [convertOpen, setConvertOpen] = useState(false)
  const [convertPending, setConvertPending] = useState(false)
  const { i18n } = useLingui()
  const mode = useExperienceMode(experienceMode)
  const recordedAs = recordingModeOf(meeting.template_id)
  const id = meetingId
  const { onRenameSubject, onToggleHidden, onDelete } = useMeetingLifecycleActions({
    company,
    meetingId,
    orgId,
    queryClient,
    refetch,
    navigateHome,
    isOrgAdmin,
    isOrganizer,
    hidden: meeting.hidden ?? false,
  })

  const handleChangeSourceLanguage = useCallback(
    (code: string): void => {
      if (!company) return
      setIsChangingSourceLanguage(true)
      void setMeetingLanguage(company, id, code)
        .then(() => {
          notifications.show({
            message: i18n._(msg`Regenerating the summary in the new language.`),
            color: "success",
          })
          void queryClient.invalidateQueries({ queryKey: queryKeys.meeting(orgId, id) })
          void refetch()
        })
        .catch((err: unknown) => {
          notifyError(err, i18n._(msg`Could not change meeting language`))
        })
        .finally(() => {
          setIsChangingSourceLanguage(false)
        })
    },
    [company, i18n, id, orgId, queryClient, refetch],
  )

  return (
    <SpeakerColorsProvider colors={speakerColorMap}>
      <Container size="lg">
        <MeetingAccessModal opened={accessOpened} onClose={closeAccess} meetingId={id} onChanged={() => refetch()} />
        {recordedAs && (
          <MeetingRecordingMismatchBanner
            recordedAs={recordedAs}
            defaultForNew={mode.current}
            onChangeDefault={mode.setMode}
            changing={mode.isPending}
          />
        )}
        {meeting.can_edit && analysisLocale !== sourceLocale && (
          <Alert color="info" variant="light" mb="md">
            <Group justify="space-between" gap="sm" wrap="nowrap">
              <Text size="sm">
                <Trans>You're viewing a translation. To edit the summary, switch to the original language.</Trans>
              </Text>
              <Button size="xs" variant="light" style={{ flexShrink: 0 }} onClick={() => onLocaleChange(sourceLocale)}>
                <Trans>Switch to original</Trans>
              </Button>
            </Group>
          </Alert>
        )}
        <MeetingSummaryView
          reveal={reveal}
          initialSeekSeconds={seekSeconds}
          initialTab={initialTab}
          meeting={meeting}
          speakers={speakerNameList}
          speakerVMs={speakerVMs}
          analysis={
            normalizeAnalysis(
              displayAnalysis,
              speakerLabelDisplayMap(speakerVMs.speakers),
            ) as unknown as IMeetingAnalysis
          }
          companyId={companyId ?? undefined}
          speakerColors={speakerColorMap}
          onSpeakerEdited={() => refetch()}
          onRenameSubject={onRenameSubject}
          languageContext={{
            sourceLocale,
            availableTranslations,
            currentLocale: analysisLocale,
            isTranslating,
            hasTranslationError,
            onLocaleChange,
            onChangeSourceLanguage: company && canReprocess(meeting.status) ? handleChangeSourceLanguage : undefined,
            isChangingSourceLanguage,
          }}
          editContext={
            analysisLocale === sourceLocale
              ? ({
                  canEdit: meeting.can_edit ?? false,
                  fieldEdits: meeting.field_edits ?? {},
                  meetingId: id,
                  companyId,
                  onEdited: () => refetch(),
                } satisfies IEditContext)
              : undefined
          }
          actions={{
            onManageAccess: canManageAccess ? openAccess : undefined,
            onToggleHidden,
            isHidden: meeting.hidden,
            onDelete,
            onConvertToLite: canManageAccess ? () => setConvertOpen(true) : undefined,
          }}
          jargonSuggestions={jargonSuggestions}
          onAcceptJargon={onAcceptJargon}
          onDismissJargon={onDismissJargon}
          jargonBusyId={jargonBusyId}
        />
        <ConvertRecordingModeModal
          opened={convertOpen}
          onClose={() => setConvertOpen(false)}
          target="lite"
          hasAnalysis
          estimateCredits={null}
          loading={convertPending}
          onConfirm={() => {
            if (!company) return
            setConvertPending(true)
            void convertMeetingRecordingMode(company, id, "lite")
              .then(() => {
                setConvertOpen(false)
                notifications.show({
                  message: i18n._(msg`This meeting is now Lite. The summary is hidden, not deleted.`),
                  color: "success",
                })
                void refetch()
              })
              .catch((err: unknown) => notifyError(err, i18n._(msg`Could not convert this meeting`)))
              .finally(() => setConvertPending(false))
          }}
        />
      </Container>
    </SpeakerColorsProvider>
  )
}
